import { useMemo, useState } from 'react';

import type { ISortingFunctions, ISort, ITableSortingState } from '@/components/common/table/types';

type IUseTableSortConfig<K extends string, V> = {
  initial?: ITableSortingState<K>;
  rawData?: V[];
  sortingFunctions: ISortingFunctions<K, V>;
};

type IUseTableSortReturnType<K extends string, V> = {
  sortedData: V[];
  onSort: (columnName: K) => void;
  setRawData: (data: V[]) => void;
  sortingState: ITableSortingState<K>;
};

const toggleSort = (state?: ISort): ISort | undefined => {
  switch (state) {
    case undefined:
      return 'ASC';
    case 'ASC':
      return 'DESC';
    case 'DESC':
      return undefined;
  }
};

export function useTableSort<K extends string, V>(params: IUseTableSortConfig<K, V>): IUseTableSortReturnType<K, V> {
  const [sortingState, setSortingState] = useState<ITableSortingState<K>>(params.initial || {});
  const [rawData, setRawData] = useState<V[]>(params.rawData || []);
  const sortedData = useMemo<V[]>(() => {
    if (!rawData.length) return [];

    const sort = (Object.entries(sortingState) as [K, ISort][]).find(([_, state]) => state);
    const rawSorted = [...rawData];

    if (!sort || !sort[1]) {
      return rawSorted;
    }

    const [colName, sortState] = sort;

    const sortingFunction = params.sortingFunctions[colName];
    rawSorted.sort((resA, resB) => {
      const [res1, res2] = sortState === 'DESC' ? [resB, resA] : [resA, resB];
      return sortingFunction(res1, res2);
    });

    return rawSorted;
  }, [sortingState, params.sortingFunctions, rawData]);

  const onSort = (colName: K) => {
    setSortingState(sort => ({ [colName]: toggleSort(sort[colName]) } as ITableSortingState<K>));
  };

  return { sortedData, onSort, setRawData, sortingState };
}
